import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { useTelegram } from '../../hooks';

interface BalanceAdjustmentData {
  type: 'credit' | 'debit';
  amount: number;
  newBalance: number;
  reason: string | null;
}

interface BalanceAdjustmentPopupProps {
  data: BalanceAdjustmentData | null;
  onClose: () => void;
}

export function BalanceAdjustmentPopup({ data, onClose }: BalanceAdjustmentPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { hapticNotification } = useTelegram();

  useEffect(() => {
    if (data) {
      setIsVisible(true);
      // Haptic feedback
      hapticNotification(data.type === 'credit' ? 'success' : 'warning');
    }
  }, [data, hapticNotification]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  if (!data) return null;

  const isCredit = data.type === 'credit';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="relative w-full max-w-sm overflow-hidden"
          >
            {/* Main Card */}
            <div className={`relative rounded-3xl ${
              isCredit 
                ? 'bg-gradient-to-br from-emerald-900/90 via-emerald-800/90 to-green-900/90' 
                : 'bg-gradient-to-br from-orange-900/90 via-red-800/90 to-rose-900/90'
            } border ${
              isCredit ? 'border-emerald-500/30' : 'border-orange-500/30'
            } shadow-2xl`}>
              
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
              >
                <X className="w-5 h-5 text-white/80" />
              </button>

              {/* Icon Animation */}
              <div className="flex justify-center pt-8 pb-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2, damping: 10 }}
                  className={`relative p-6 rounded-full ${
                    isCredit 
                      ? 'bg-gradient-to-br from-emerald-400 to-green-500 shadow-lg shadow-emerald-500/50' 
                      : 'bg-gradient-to-br from-orange-400 to-red-500 shadow-lg shadow-orange-500/50'
                  }`}
                >
                  {isCredit ? (
                    <TrendingUp className="w-10 h-10 text-white" />
                  ) : (
                    <TrendingDown className="w-10 h-10 text-white" />
                  )}
                  
                  {/* Sparkle Effects for Credit */}
                  {isCredit && (
                    <>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 1, 0.5],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -top-2 -right-2"
                      >
                        <Sparkles className="w-6 h-6 text-yellow-300" />
                      </motion.div>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.3, 0.8, 0.3],
                        }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                        className="absolute -bottom-1 -left-2"
                      >
                        <Sparkles className="w-4 h-4 text-yellow-200" />
                      </motion.div>
                    </>
                  )}
                </motion.div>
              </div>

              {/* Content */}
              <div className="text-center px-6 pb-6">
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold text-white mb-2"
                >
                  {isCredit ? '🎉 Balance Credited!' : '📢 Balance Adjusted'}
                </motion.h2>

                {/* Amount */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                  className={`text-5xl font-extrabold my-4 ${
                    isCredit ? 'text-emerald-300' : 'text-orange-300'
                  }`}
                >
                  {isCredit ? '+' : '-'}{data.amount.toLocaleString()}
                  <span className="text-xl ml-2 text-white/60">points</span>
                </motion.div>

                {/* Reason */}
                {data.reason && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white/10 rounded-xl px-4 py-3 mb-4"
                  >
                    <p className="text-sm text-white/60 mb-1">Reason</p>
                    <p className="text-white font-medium">{data.reason}</p>
                  </motion.div>
                )}

                {/* New Balance */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white/5 rounded-xl px-4 py-3 mb-6"
                >
                  <p className="text-sm text-white/60 mb-1">New Balance</p>
                  <p className="text-2xl font-bold text-white">
                    💰 {data.newBalance.toLocaleString()} <span className="text-sm font-normal text-white/60">points</span>
                  </p>
                </motion.div>

                {/* Action Button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  onClick={handleClose}
                  className={`w-full py-4 rounded-2xl font-bold text-white transition-all ${
                    isCredit 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 shadow-lg shadow-emerald-500/30' 
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 shadow-lg shadow-orange-500/30'
                  }`}
                >
                  {isCredit ? 'Awesome! 🚀' : 'Got it'}
                </motion.button>
              </div>

              {/* Decorative Background Elements */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl ${
                  isCredit ? 'bg-emerald-400/20' : 'bg-orange-400/20'
                }`} />
                <div className={`absolute -bottom-20 -left-20 w-40 h-40 rounded-full blur-3xl ${
                  isCredit ? 'bg-green-400/20' : 'bg-red-400/20'
                }`} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
